import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/RelatorioDesperdicio.css";
import {
  FaArrowLeft,
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import SelectBoxResponsavelPesquisa from "./SelectBoxResponsavelPesquisa";
import { useUser } from "../context/UserContext";
import BackButton from "../components/BackButton";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

registerLocale("pt-BR", ptBR);

const RelatorioDesperdicio = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useUser();
  const { setIsLoading } = useLoading();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dataInicio, setDataInicio] = useState(firstDayOfMonth);
  const [dataFim, setDataFim] = useState(lastDayOfMonth);
  const [responsavelSelecionado, setResponsavelSelecionado] = useState("");
  const [nomeIngrediente, setNomeIngrediente] = useState("");
  const [chartData, setChartData] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [biAnalysis, setBiAnalysis] = useState(null);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [detalhes, setDetalhes] = useState([]);
  const [buscaRealizada, setBuscaRealizada] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    resumo: true,
    analise: true,
    detalhes: true,
  });

  useEffect(() => {
    handleSearch();
  }, [id, user]);

  const handleSearch = async () => {
    const toastId = toast.loading("Buscando relatório...");
    setIsLoading(true);
    setBuscaRealizada(true);

    const inicio = dataInicio || firstDayOfMonth;
    const fim = dataFim || lastDayOfMonth;

    const formattedDataInicio = inicio.toISOString().split("T")[0];
    const formattedDataFim = fim.toISOString().split("T")[0];

    let apiUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/relatorios/desperdicio?idRestaurante=${id}`;
    apiUrl += `&dataInicio=${formattedDataInicio}`;
    apiUrl += `&dataFim=${formattedDataFim}`;

    if (nomeIngrediente) {
      apiUrl += `&nomeIngrediente=${nomeIngrediente}`;
    }
    if (responsavelSelecionado) {
      apiUrl += `&idResponsavel=${responsavelSelecionado}`;
    }

    try {
      const token = user?.access_token;
      if (!token) {
        console.error("Token de autenticação não encontrado.");
        toast.update(toastId, {
          render: "Token de autenticação não encontrado.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        setIsLoading(false);
        return;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authentication: token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const rawData = await response.json();

      if (rawData && rawData.detalhes) {
        const ingredientesMap = new Map();
        rawData.detalhes.forEach((item) => {
          if (!ingredientesMap.has(item.nomeIngrediente)) {
            ingredientesMap.set(item.nomeIngrediente, 0);
          }
          ingredientesMap.set(
            item.nomeIngrediente,
            ingredientesMap.get(item.nomeIngrediente) + (item.descarte != null ? item.descarte : 0.0)
          );
        });

        const chartDataArray = Array.from(ingredientesMap).map(
          ([name, value]) => ({
            name,
            value: parseFloat(value.toFixed(2)),
          })
        );

        setChartData(chartDataArray);
        setIngredientes(rawData.resumo?.ingredientes || []);
        setDetalhes(rawData.detalhes);
      }

      if (rawData && rawData.resumo) {
        setBiAnalysis({
          totalDescarte: rawData.resumo.totalDescarte || 0,
          ingredientes: rawData.resumo.ingredientes || [],
        });
      }
      toast.update(toastId, {
        render: "Relatório carregado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Erro ao buscar relatório de desperdício:", error);
      setChartData([]);
      setDetalhes([]);
      toast.update(toastId, {
        render: "Erro ao buscar relatório de desperdício.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString("pt-BR");
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const barColors = ["#d32f2f", "#f44336", "#e57373", "#ef9a9a", "#ffcdd2"];

  return (
    <div className="relatorio-desperdicio-container">
      <ToastContainer />
      <BackButton inline />
      <h2>Relatório de Desperdício</h2>

      <div className="filtro-container-disperdicio">
        <div className="filtro-group">
          <label>Data Início:</label>
          <DatePicker
            selected={dataInicio}
            onChange={(date) => setDataInicio(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            className="date-picker"
            placeholderText="Selecione a data de início"
          />
        </div>
        <div className="filtro-group">
          <label>Data Fim:</label>
          <DatePicker
            selected={dataFim}
            onChange={(date) => setDataFim(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            className="date-picker"
            placeholderText="Selecione a data de fim"
          />
        </div>
        <div className="filtro-group">
          <label>Nome do Ingrediente:</label>
          <input
            type="text"
            value={nomeIngrediente}
            onChange={(e) => setNomeIngrediente(e.target.value)}
            className="text-input"
            placeholder="Digite o nome do ingrediente"
          />
        </div>
        <div className="filtro-group">
          <label>Responsável:</label>
          <SelectBoxResponsavelPesquisa
            value={responsavelSelecionado}
            onChange={setResponsavelSelecionado}
            className="select-box"
          />
        </div>
        <button
          onClick={handleSearch}
          className={isLoadingLocal ? "search-button loading" : "search-button"}
          disabled={isLoadingLocal}
        >
          <FaSearch /> {isLoadingLocal ? "Buscando..." : "Buscar"}
        </button>
      </div>

      <div className="relatorio-content">
        {isLoadingLocal ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Carregando dados de desperdício...</p>
          </div>
        ) : (
          buscaRealizada && (
            <>
              <div className="section-container">
                <div
                  className="section-header"
                  onClick={() => toggleSection("resumo")}
                >
                  <h3>Resumo de Desperdício</h3>
                  {expandedSections.resumo ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </div>
                {expandedSections.resumo && (
                  <div className="metrics-grid">
                    <div className="metric-card desperdicio">
                      <span className="metric-value desperdicio">
                        {biAnalysis?.totalDescarte.toFixed(2)} kg
                      </span>
                      <span className="metric-label">Total Descartado</span>
                    </div>
                    <div className="metric-card desperdicio">
                      <span className="metric-value desperdicio">
                        {ingredientes.length}
                      </span>
                      <span className="metric-label">
                        Ingredientes com Desperdício
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="section-container">
                <div
                  className="section-header"
                  onClick={() => toggleSection("analise")}
                >
                  <h3>Análise por Ingrediente</h3>
                  {expandedSections.analise ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </div>
                {expandedSections.analise && (
                  <div className="charts-container">
                    <div className="chart-wrapper">
                      <h4>Desperdício por Ingrediente (kg)</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={chartData}
                          margin={{
                            top: 20,
                            right: 30,
                            left: 20,
                            bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar
                            dataKey="value"
                            name="Desperdício (kg)"
                            fill={barColors[0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              <div className="section-container detalhes-section">
                <div
                  className="section-header"
                  onClick={() => toggleSection("detalhes")}
                >
                  <h3>Detalhes dos Descartes</h3>
                  {expandedSections.detalhes ? (
                    <FaChevronUp />
                  ) : (
                    <FaChevronDown />
                  )}
                </div>
                {expandedSections.detalhes && (
                  <div className="table-container">
                    <table className="rotinas-table desperdicio-table">
                      <thead>
                        <tr>
                          <th>Ingrediente</th>
                          <th>Data</th>
                          <th>Quantidade (kg)</th>
                          <th>Responsável</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalhes.length > 0 ? (
                          detalhes.map((item, index) => (
                            <tr key={index}>
                              <td>{item.nomeIngrediente}</td>
                              <td>{formatarData(item.data)}</td>
                              <td>{item.descarte != null ? item.descarte.toFixed(2) : 0.0.toFixed(2)}</td>
                              <td>{item.responsavel}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>
                              Nenhum registro de desperdício encontrado
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default RelatorioDesperdicio;
